/**
 * The task rules, frozen and versioned. Sprint 1.9.4 S4 / BSV-017 items 1–5.
 *
 * Everything the MEASUREMENT depends on lives in this object and nowhere else:
 * the two presentations receive events from the runner and cannot reach back
 * into it. A change to any figure here is a new `version`, and a trace records
 * the version it was made under, so two attempts under different rules are
 * never compared as if they were the same task.
 *
 * The figures are the brief psychomotor vigilance test's (PVT-B, Basner,
 * Mollicone & Dinges 2011): three minutes, a uniformly distributed foreperiod
 * of 1–4 s, a false start for any response before the stimulus or within
 * 100 ms of it, a lapse at 355 ms, a stimulus that persists until a response
 * or 30 s. See `docs/beacon-protocol.md` for the sources and the bounded claim.
 */

export interface Protocol {
  /** Bumped whenever any figure below changes. */
  readonly version: string
  /** The measurement run's length. */
  readonly durationMs: number
  /** The practice run's length. Practice never enters a history. */
  readonly practiceDurationMs: number
  /** Foreperiod (inter-stimulus interval) drawn uniformly, whole milliseconds, inclusive. */
  readonly foreperiodMs: { readonly min: number; readonly max: number }
  /** A response this soon after onset is anticipation, not a reaction. */
  readonly falseStartMaxMs: number
  /** A reaction at or above this is a lapse. PVT-B's figure, not the 10-minute PVT's 500 ms. */
  readonly lapseMs: number
  /** A lit stimulus with no response for this long is a timeout (counted as a lapse). */
  readonly timeoutMs: number
  /** Below this many valid trials the attempt is not scored. From the budget. */
  readonly minValidTrials: number
  /** A response event whose timestamp is older than this on arrival is stale. */
  readonly staleEventMs: number
  /** A frame gap longer than this in the foreperiod-to-onset window invalidates the trial's onset. */
  readonly frameStallMs: number
}

export const PROTOCOL: Protocol = Object.freeze({
  version: 'pvt-b-browser/1.0',
  durationMs: 180_000,
  practiceDurationMs: 45_000,
  foreperiodMs: Object.freeze({ min: 1000, max: 4000 }),
  falseStartMaxMs: 100,
  lapseMs: 355,
  timeoutMs: 30_000,
  minValidTrials: 40,
  staleEventMs: 50,
  frameStallMs: 50,
})

/**
 * What a presentation is allowed to differ in. Recorded in the trace beside
 * the protocol version, so decoration is a named experimental condition and
 * not something that happened.
 */
export interface PresentationVersion {
  readonly id: 'plain' | 'beacon'
  readonly version: string
  /** Whether points are shown during the run. Feedback, never a measure. */
  readonly pointsShown: boolean
  /** Whether the reaction time is shown briefly after each response (the original PVT does). */
  readonly rtShown: boolean
  readonly sound: boolean
}

export const PLAIN_PRESENTATION: PresentationVersion = Object.freeze({
  id: 'plain',
  version: 'plain/1.0',
  pointsShown: false,
  rtShown: true,
  sound: false,
})

export const BEACON_PRESENTATION: PresentationVersion = Object.freeze({
  id: 'beacon',
  version: 'beacon/1.0',
  pointsShown: true,
  rtShown: false,
  sound: false,
})

/**
 * Arcade points, presentation v1.0. Feedback only: computed from the reaction
 * time the runner already measured, displayed, and NOT stored as a measure.
 * Changing this table is a new presentation version and moves no measurement.
 */
export function beaconPoints(rtMs: number | null, outcome: string): number {
  if (outcome !== 'response' || rtMs === null) return 0
  if (rtMs < 250) return 100
  if (rtMs < 355) return 60
  return 20
}
